import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverAnchor } from './ui/popover';
import { Globe2, ArrowLeftRight, Search, Calendar as CalIcon, Sparkles } from 'lucide-react';

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Typeable country picker (type to filter, or pick from the list).
const CountryCombobox = ({ label, value, countries, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [display, setDisplay] = useState('');
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);
  const sel = countries.find((c) => c.country_code === value);

  useEffect(() => { if (!editing) setDisplay(sel ? `${sel.flag} ${sel.name}` : ''); }, [value, editing, sel]);

  const ql = q.trim().toLowerCase();
  const list = (ql
    ? countries.filter((c) => c.name.toLowerCase().includes(ql) || c.country_code.toLowerCase().includes(ql))
    : countries).slice(0, 80);

  const pick = (c) => { onChange(c.country_code); setEditing(false); setDisplay(`${c.flag} ${c.name}`); setQ(''); setOpen(false); };
  const beginEdit = () => { if (!editing) { setEditing(true); setDisplay(''); setQ(''); } setOpen(true); };
  const handleOpen = (n) => { setOpen(n); if (!n) { setEditing(false); setDisplay(sel ? `${sel.flag} ${sel.name}` : ''); } };

  return (
    <div className="relative" ref={ref}>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</label>
      <Popover open={open} onOpenChange={handleOpen}>
        <PopoverAnchor asChild>
          <div className="flex items-center gap-2 rounded-xl border border-input bg-card px-3 h-12 focus-within:ring-2 focus-within:ring-accent">
            <Globe2 className="h-4 w-4 text-accent shrink-0" />
            <input
              data-testid={testid}
              className="w-full bg-transparent outline-none text-sm font-medium placeholder:text-muted-foreground/70"
              placeholder="Type a country"
              value={display}
              onFocus={beginEdit}
              onMouseDown={beginEdit}
              onChange={(e) => { setEditing(true); setDisplay(e.target.value); setQ(e.target.value); setOpen(true); }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[240px] max-h-72 overflow-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => { if (ref.current && ref.current.contains(e.target)) e.preventDefault(); }}>
          {list.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">No countries found</p>
          ) : list.map((c) => (
            <button key={c.country_code} onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2"
              data-testid={`country-option-${c.country_code}`}>
              <span>{c.flag}</span><span className="flex-1 truncate">{c.name}</span>
              <span className="text-xs font-mono text-muted-foreground">{c.country_code}</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const CountrySearchBar = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [from, setFrom] = useState('IN');
  const [to, setTo] = useState('ID');
  const [mode, setMode] = useState('explore');
  const [date, setDate] = useState(todayPlus(30));
  const [error, setError] = useState('');

  useEffect(() => { api_load(); }, []);
  const api_load = async () => {
    try {
      const { api } = await import('../lib/api');
      const { data } = await api.countryMeta();
      setCountries(data.countries || []);
    } catch { /* non-fatal */ }
  };

  const swap = () => { setFrom(to); setTo(from); };
  const submit = () => {
    if (!from || !to) { setError('Select both countries.'); return; }
    if (from === to) { setError('Origin and destination country cannot be the same.'); return; }
    setError('');
    const q = new URLSearchParams({ from, to, mode, date: mode === 'live' ? date : '' }).toString();
    navigate(`/flights/country-results?${q}`);
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-4 sm:p-5 card-elevate" data-testid="country-search-bar">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent bg-accent/10 px-2.5 py-1 rounded-full"><Globe2 className="h-3.5 w-3.5" /> Country explorer</span>
        <div className="flex items-center gap-1">
          {[['explore', 'Explore (flexible)'], ['live', 'Exact date']].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${mode === v ? 'bg-accent/12 text-accent border border-accent/30' : 'text-muted-foreground hover:bg-secondary border border-transparent'}`}
              data-testid={`country-mode-${v}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-12 items-end">
        <div className="md:col-span-4">
          <CountryCombobox label="From country" value={from} countries={countries} onChange={setFrom} testid="country-from" />
        </div>
        <div className="md:col-span-4 relative">
          <CountryCombobox label="To country" value={to} countries={countries} onChange={setTo} testid="country-to" />
          <button onClick={swap} title="Swap" className="hidden md:grid place-items-center absolute -left-5 top-7 h-9 w-9 rounded-full bg-card border border-border shadow-sm hover:bg-secondary z-10" data-testid="country-swap">
            <ArrowLeftRight className="h-4 w-4 text-accent" />
          </button>
        </div>
        <div className="md:col-span-2">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Date</label>
          <div className={`flex items-center gap-2 rounded-xl border border-input px-3 h-12 ${mode === 'explore' ? 'bg-secondary/60 opacity-60' : 'bg-card'}`}>
            <CalIcon className="h-4 w-4 text-accent" />
            <input type="date" value={date} min={todayPlus(0)} disabled={mode === 'explore'} onChange={(e) => setDate(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium disabled:cursor-not-allowed" data-testid="country-date" />
          </div>
        </div>
        <div className="md:col-span-2">
          <Button onClick={submit} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold rounded-xl" data-testid="country-search-submit">
            <Search className="h-5 w-5 mr-2" /> Explore
          </Button>
        </div>
      </div>
      {mode === 'explore' && (
        <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-accent" /> Explore mode shows indicative "best price we found" fares across candidate airports. Pick an exact date for a dated search.</p>
      )}
      {error && <p className="mt-2 text-sm text-destructive" data-testid="country-search-error">{error}</p>}
    </div>
  );
};
